import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, User } from '../types';
import { authHelpers, supabase } from '../lib/supabase';
import { initializeUserKeyPair, getUserPublicKeyBase64 } from '../lib/crypto';
import { logAuthEvent, logSessionStart, logSessionEnd } from '../lib/auditLogger';
import toast from 'react-hot-toast';

interface AuthStore extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  loginWithWallet: (walletAddress: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  initializeAuth: () => Promise<void>;
  clearAuthState: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      clearAuthState: () => {
        set({ user: null, isAuthenticated: false, isLoading: false, error: null });
      },

      initializeAuth: async () => {
        try {
          set({ isLoading: true, error: null });
          
          // Increased timeout to 30 seconds to handle network latency
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Auth initialization timeout')), 30000);
          });

          const authPromise = (async () => {
            try {
              const { session, error: sessionError } = await authHelpers.getSession();
              
              if (sessionError) {
                console.error('Session error during initialization:', sessionError);
                set({ user: null, isAuthenticated: false, isLoading: false });
                return;
              }

              if (session?.user) {
                const user: User = {
                  id: session.user.id,
                  email: session.user.email || '',
                  name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
                  role: 'user',
                  isVerified: session.user.email_confirmed_at !== null,
                  createdAt: new Date(session.user.created_at),
                };
                
                // Initialize crypto in background (non-blocking)
                initializeUserCrypto(user).catch(error => {
                  console.error('Failed to initialize user crypto (non-blocking):', error);
                });
                
                // Log session restoration (non-blocking)
                logAuthEvent('login', 'success', {
                  action_type: 'session_restored',
                  user_id: user.id
                }).catch(error => {
                  console.error('Failed to log auth event (non-blocking):', error);
                });
                
                set({ user, isAuthenticated: true, isLoading: false });
              } else {
                set({ user: null, isAuthenticated: false, isLoading: false });
              }
            } catch (innerError: any) {
              console.error('Inner auth promise error:', innerError);
              set({ user: null, isAuthenticated: false, isLoading: false });
            }
          })();

          await Promise.race([authPromise, timeoutPromise]);
          
        } catch (error: any) {
          console.error('Auth initialization error:', error);
          
          // Check for specific stale session error
          if (error.message && error.message.includes('Session from session_id claim in JWT does not exist')) {
            console.log('Detected stale session, clearing authentication tokens...');
            
            // Clear all Supabase auth tokens from localStorage
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
                keysToRemove.push(key);
              }
            }
            
            keysToRemove.forEach(key => {
              localStorage.removeItem(key);
            });
            
            // Clear Supabase session
            try {
              await supabase.auth.signOut();
            } catch (signOutError) {
              console.error('Error clearing stale session:', signOutError);
            }
            
            // Force page reload to reinitialize with clean state
            window.location.reload();
            return;
          }
          
          // Log failed initialization (non-blocking)
          logAuthEvent('login', 'failed', {
            action_type: 'session_initialization_failed',
            error: error.message
          }).catch(logError => {
            console.error('Failed to log auth event (non-blocking):', logError);
          });
          
          // Clear local auth state
          set({ 
            user: null, 
            isAuthenticated: false, 
            isLoading: false,
            error: error.message === 'Auth initialization timeout' ? 'Authentication timeout - please refresh the page' : null
          });
          
          // Always clear Supabase session to remove any corrupted tokens
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            console.error('Error clearing stale session:', signOutError);
          }
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const { data, error } = await authHelpers.signIn(email, password);
          
          if (error) {
            // Log failed login
            await logAuthEvent('login', 'failed', {
              email,
              error: error.message,
              action_type: 'email_password_login'
            });
            throw new Error(error.message);
          }

          if (data.user) {
            const user: User = {
              id: data.user.id,
              email: data.user.email || '',
              name: data.user.user_metadata?.name || data.user.email?.split('@')[0] || 'User',
              role: 'user',
              isVerified: data.user.email_confirmed_at !== null,
              createdAt: new Date(data.user.created_at),
            };

            initializeUserCrypto(user).catch(error => {
              console.error('Failed to initialize user crypto (non-blocking):', error);
            });

            // Log successful login and start session
            await logAuthEvent('login', 'success', {
              email,
              user_id: user.id,
              action_type: 'email_password_login'
            });
            
            await logSessionStart();

            set({ user, isAuthenticated: true, isLoading: false });
            toast.success('Successfully signed in!');
          }
        } catch (error: any) {
          const errorMessage = error.message || 'Login failed';
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
        }
      },

      signup: async (email: string, password: string, name?: string) => {
        set({ isLoading: true, error: null });
        try {
          const { data, error } = await authHelpers.signUp(email, password);
          
          if (error) {
            // Log failed signup
            await logAuthEvent('signup', 'failed', {
              email,
              error: error.message,
              action_type: 'email_password_signup'
            });
            throw new Error(error.message);
          }

          if (data.user) {
            // Log successful signup
            await logAuthEvent('signup', 'success', {
              email,
              user_id: data.user.id,
              action_type: 'email_password_signup'
            });
            
            if (data.user.email_confirmed_at) {
              const user: User = {
                id: data.user.id,
                email: data.user.email || '',
                name: name || data.user.email?.split('@')[0] || 'User',
                role: 'user',
                isVerified: true,
                createdAt: new Date(data.user.created_at),
              };
              
              initializeUserCrypto(user).catch(error => {
                console.error('Failed to initialize user crypto (non-blocking):', error);
              });
              
              await logSessionStart();
              
              set({ user, isAuthenticated: true, isLoading: false });
              toast.success('Account created successfully!');
            } else {
              set({ isLoading: false });
              toast.success('Account created! Please check your email to verify your account.');
            }
          }
        } catch (error: any) {
          const errorMessage = error.message || 'Signup failed';
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
        }
      },

      loginWithWallet: async (walletAddress: string) => {
        set({ isLoading: true, error: null });
        try {
          // In a real implementation, this would verify the wallet signature
          // For now, we'll create a user session based on the wallet address
          
          // Check if user exists with this wallet address
          const { data: existingUser, error: fetchError } = await supabase
            .from('users')
            .select('*')
            .eq('wallet_address', walletAddress)
            .single();

          if (fetchError && fetchError.code !== 'PGRST116') {
            throw new Error('Failed to check existing wallet user');
          }

          let user: User;

          if (existingUser) {
            // User exists, create session
            user = {
              id: existingUser.id,
              email: existingUser.email || '',
              name: existingUser.name || `User ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
              walletAddress,
              role: 'user',
              isVerified: true,
              createdAt: new Date(existingUser.created_at),
            };
          } else {
            // Create new user with wallet address
            const newUserId = crypto.randomUUID();
            const { error: insertError } = await supabase
              .from('users')
              .insert({
                id: newUserId,
                email: `${walletAddress}@wallet.local`,
                name: `User ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
                wallet_address: walletAddress,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });

            if (insertError) {
              throw new Error('Failed to create wallet user');
            }

            user = {
              id: newUserId,
              email: `${walletAddress}@wallet.local`,
              name: `User ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
              walletAddress,
              role: 'user',
              isVerified: true,
              createdAt: new Date(),
            };
          }

          // Log wallet login
          await logAuthEvent('login', 'success', {
            wallet_address: walletAddress,
            user_id: user.id,
            action_type: 'wallet_login'
          });
          
          await logSessionStart();

          set({ user, isAuthenticated: true, isLoading: false });
          toast.success('Wallet connected successfully!');
        } catch (error: any) {
          const errorMessage = error.message || 'Wallet connection failed';
          
          // Log failed wallet login
          await logAuthEvent('login', 'failed', {
            wallet_address: walletAddress,
            error: errorMessage,
            action_type: 'wallet_login'
          });
          
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
        }
      },

      logout: async () => {
        try {
          const { user } = get();
          
          // Log session end and logout
          await logSessionEnd();
          await logAuthEvent('logout', 'success', {
            user_id: user?.id,
            action_type: 'manual_logout'
          });
          
          const { error } = await authHelpers.signOut();
          if (error) {
            throw new Error(error.message);
          }
          set({ user: null, isAuthenticated: false, error: null });
          toast.success('Successfully signed out!');
        } catch (error: any) {
          console.error('Logout error:', error);
          
          // Log failed logout
          await logAuthEvent('logout', 'failed', {
            error: error.message,
            action_type: 'manual_logout'
          });
          
          set({ user: null, isAuthenticated: false, error: null });
          toast.error('Error signing out');
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...userData } });
        }
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({ 
        // Don't persist anything to avoid stale state
      }),
    }
  )
);

/**
 * Initialize user's cryptographic keys and update their profile (non-blocking)
 */
const initializeUserCrypto = async (user: User) => {
  try {
    await initializeUserKeyPair();
    
    const publicKey = await getUserPublicKeyBase64();
    
    if (publicKey) {
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('id, email, public_key')
        .eq('id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Failed to check existing user:', fetchError);
        return;
      }

      if (existingUser) {
        if (!existingUser.public_key || existingUser.public_key !== publicKey) {
          const { error: updateError } = await supabase
            .from('users')
            .update({
              name: user.name,
              public_key: publicKey,
              wallet_address: user.walletAddress || null,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);

          if (updateError) {
            console.error('Failed to update user profile with public key:', updateError);
          }
        }
      } else {
        const { error: insertError } = await supabase
          .from('users')
          .insert({
            id: user.id,
            email: user.email,
            name: user.name,
            public_key: publicKey,
            wallet_address: user.walletAddress || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Failed to create user profile with public key:', insertError);
        }
      }
    }
  } catch (error: any) {
    console.error('Failed to initialize user crypto:', error);
  }
};

// Set up auth state listener
supabase.auth.onAuthStateChange(async (event, session) => {
  try {
    const store = useAuthStore.getState();
    
    if (event === 'SIGNED_IN' && session?.user) {
      const user: User = {
        id: session.user.id,
        email: session.user.email || '',
        name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
        role: 'user',
        isVerified: session.user.email_confirmed_at !== null,
        createdAt: new Date(session.user.created_at),
      };
      
      initializeUserCrypto(user).catch(error => {
        console.error('Failed to initialize user crypto (non-blocking):', error);
      });
      
      // Log auth state change
      logAuthEvent('login', 'success', {
        user_id: user.id,
        action_type: 'auth_state_change',
        event
      }).catch(error => {
        console.error('Failed to log auth event (non-blocking):', error);
      });
      
      useAuthStore.setState({ user, isAuthenticated: true, isLoading: false });
    } else if (event === 'SIGNED_OUT') {
      // Log auth state change
      logAuthEvent('logout', 'success', {
        action_type: 'auth_state_change',
        event
      }).catch(error => {
        console.error('Failed to log auth event (non-blocking):', error);
      });
      
      useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
    } else if (event === 'TOKEN_REFRESHED' && session?.user) {
      const user: User = {
        id: session.user.id,
        email: session.user.email || '',
        name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
        role: 'user',
        isVerified: session.user.email_confirmed_at !== null,
        createdAt: new Date(session.user.created_at),
      };
      useAuthStore.setState({ user, isAuthenticated: true, isLoading: false });
    }
  } catch (error) {
    console.error('Auth state change error:', error);
    useAuthStore.setState({ isLoading: false });
    
    // Clear any corrupted tokens on auth state change errors
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      console.error('Error clearing session after auth state change error:', signOutError);
    }
  }
});